









































import {
    SdsDividerLine,
    SdsSidebar,
    SdsSidebarItem,
} from '@superbet-group/web.lib.ui-components';
import {
    useVueRouter,
    useVuexStore,
} from '@superbet-group/web.lib.vue-utilities';
import {
    computed,
    ComputedRef,
    defineComponent,
} from '@vue/composition-api';

import { RouteName } from '@app/router/constants/RouteName';
import { AuthenticationStore } from '@store/modules/data/authentication/authenticationIndex';
import { InboxStore } from '@store/modules/ui/inbox/inboxIndex';
import { AccountStore } from '@store/modules/ui/user/account/accountIndex';
import { shellLocalConfig } from '@src/config/shell/localConfig';
import modalIndex from '@store/modules/ui/shared/modal/modalIndex';
import modalCodes from '@store/modules/ui/shared/modal/modalCodes';
import { RafClickValues } from '@core/models/referAFriend/enums';
import useReferAFriendFlag from '@shared/composables/useReferAFriendFlag';
import { useLoyaltyFlag } from '@shared/composables/useLoyaltyFlag';
import { useLoginBeforeNavigate } from '@app/router/composables/useLoginBeforeNavigate';
import { getAccountConfig } from '@accountFederation/config/accountConfig';
import { sendIdCardVerificationEventEntry } from '@accountFederation/modules/idCardVerification/analytics/idCardVerificationHub';
import { Route } from 'vue-router';
import { Analytics as AnalyticsSDK } from '@superbet-group/web.analytics.sdk';
import { ClickName } from '@superbet-group/product.analytics';
import { PromotionHubEntryEventLabels } from '@src/app/promotionHub/shared/types/PromotionHubEntryLabels';
import {
    isSocialAppEnabled,
    isSocialEnabledOrAppEnabled,
    isSocialEnabledOrAppEnabledAndIsGamingBe,
} from '@socialFederation/helpers';
import {
    AccountSidebarItem,
    SidebarItem,
} from './sidebarItems';
import { getShellConfig } from '@src/config/shell/config';

export default defineComponent({
    name: 'SdsProfileSidebar',
    setup() {
        const { route, router } = useVueRouter();
        const { isReferAFriendEnabled } = useReferAFriendFlag();
        const { isSupercoinsEnabled } = useLoyaltyFlag();
        const { loginBeforeNavigate } = useLoginBeforeNavigate();

        const authIndex = useVuexStore<AuthenticationStore>('data/authentication');
        const accountIndex = useVuexStore<AccountStore>('ui/user/account');
        const inboxIndex = useVuexStore<InboxStore>('ui/inbox');

        const numberOfNewMessages = inboxIndex.state('numberOfNewMessages');
        const userId = authIndex.getter('userId');
        const user = authIndex.getter('user');
        const logout = authIndex.action('logout');

        const modalStore = useVuexStore<typeof modalIndex>('ui/modal');
        const pushModal = modalStore.action('pushModal');

        const stickyConfig = {
            scrollableContentSelector: '.sds-profile-sidebar__wrapper',
        };

        function onPromotionsHubClick() {
            AnalyticsSDK.getInstance().log(
                'Promo_Hub',
                {
                    eventAction: 'Entry',
                    eventLabel: PromotionHubEntryEventLabels.accountSideMenu,
                    userId: userId.value?.toString(),
                },
            );
            router.value.push({ name: RouteName.BonusAndComms.PromotionHub });
        }

        function onReferAFriendClick() {
            AnalyticsSDK.getInstance().log('click', {
                click_name: ClickName.LOYALTY_RAF_ENTRYPOINT_CLICK,
                click: {
                    click_value: RafClickValues.MyAccountMenu,
                    type: 'general_click',
                },
            });

            router.value.push({ name: RouteName.Account.ReferAFriend });
        }

        function onLoyaltySupercoinsClick() {
            AnalyticsSDK.getInstance().log(
                'Loyalty_Shop_Opened',
                {
                    source: 'Account_Page',
                },
            );
            router.value.push({ name: RouteName.Loyalty.Supercoins });
        }

        async function onLogoutClick() {
            try {
                await logout();
            } finally {
                router.value.push({ name: RouteName.App.Home });
            }
        }

        const isSettingsItemActive = computed(() => {
            return [
                RouteName.Account.Settings,
                RouteName.Account.SettingsBetslip,
                RouteName.Account.SwitchLanguage,
                RouteName.BonusAndComms.MarketingConsent,
                RouteName.Account.ChangePhone,
                RouteName.Account.ChangePassword,
                RouteName.Account.LicencePreferences,
            ].includes(route.value.name!);
        });

        const hasResponsibleGaming = computed(() =>
            shellLocalConfig.getAccountSidebarMenuItems().some((item) => item.id === RouteName.Account.ResponsibleGaming),
        );

        const verifyPhone = () => {
            pushModal({
                code: getAccountConfig().hasUpliftedVerifyPhone ? modalCodes.sdsSmsValidation : modalCodes.newSmsValidation,
                isClosable: false,
                data: {
                    hasFooter: true,
                },
            });
        };

        const clickIdCardVerification = (route: Route) => {
            sendIdCardVerificationEventEntry();
            loginBeforeNavigate(route);
        };

        const filterByConfig = (sidebarItems: SidebarItem[]): SidebarItem[] => {
            if (!getShellConfig().localConfig.accountSettingsWhitelist) {
                return sidebarItems;
            }

            return sidebarItems.filter((item) => getShellConfig().localConfig.accountSettingsWhitelist?.includes(item.key));
        };

        const sidebarItems: ComputedRef<SidebarItem[]> = computed(() => (
            filterByConfig([
                {
                    key: AccountSidebarItem.BonusAndCommsNotifications,
                    label: 'pam.accountMenu.notifications',
                    iconName: 'toggle-notification',
                    to: RouteName.BonusAndComms.Notifications,
                    isEnabled: true,
                    badge: numberOfNewMessages.value > 0 ? numberOfNewMessages.value.toString() : undefined,
                },
                {
                    key: AccountSidebarItem.ChatPlaceholder,
                    label: 'pam.accountMenu.chat',
                    iconName: 'actions-send-alt',
                    to: RouteName.Account.ChatPlaceholder,
                    isEnabled: isSocialEnabledOrAppEnabled(),
                    onClick: isSocialAppEnabled() ? undefined : loginBeforeNavigate,
                },
                {
                    key: AccountSidebarItem.PromotionHub,
                    label: 'bonus_comms.promotion_hub_title',
                    iconName: 'commerce-gift',
                    to: RouteName.BonusAndComms.PromotionHub,
                    isEnabled: shellLocalConfig.bonus.isPromotionHubEntryPointVisible || !!user.value,
                    onClick: onPromotionsHubClick,
                },
                {
                    key: AccountSidebarItem.MySocial,
                    label: 'pam.accountMenu.mySocial',
                    iconName: 'human-communities',
                    to: RouteName.Account.MySocial,
                    isEnabled: isSocialEnabledOrAppEnabledAndIsGamingBe() || isSocialEnabledOrAppEnabled(),
                    onClick: loginBeforeNavigate,
                },
                {
                    key: AccountSidebarItem.LoyaltySupercoins,
                    label: 'loyalty.supercoins.title',
                    iconName: 'commerce-coins',
                    to: RouteName.Loyalty.Supercoins,
                    isEnabled: isSupercoinsEnabled.value,
                    onClick: onLoyaltySupercoinsClick,
                },
                {
                    key: AccountSidebarItem.ReferAFriend,
                    label: 'pam.accountMenu.referAFriend',
                    iconName: 'commerce-promotions',
                    to: RouteName.Account.ReferAFriend,
                    isEnabled: isReferAFriendEnabled.value,
                    onClick: onReferAFriendClick,
                },
                {
                    isDivider: true,
                    key: AccountSidebarItem.DividerSection1,
                },
                {
                    key: AccountSidebarItem.Deposit,
                    label: 'deposit',
                    iconName: 'commerce-deposit',
                    to: RouteName.Account.Deposit,
                    isEnabled: true,
                    onClick: loginBeforeNavigate,
                },
                {
                    key: AccountSidebarItem.Withdrawal,
                    label: 'withdrawal',
                    iconName: 'commerce-withdraw',
                    to: RouteName.Account.Withdrawal,
                    isEnabled: true,
                    onClick: loginBeforeNavigate,
                },
                {
                    key: AccountSidebarItem.History,
                    label: 'history',
                    iconName: 'commerce-transactions-alt',
                    to: RouteName.Account.History,
                    isEnabled: true,
                    onClick: loginBeforeNavigate,
                },
                {
                    key: AccountSidebarItem.Profile,
                    label: 'account details',
                    iconName: 'human-user',
                    to: RouteName.Account.Profile,
                    isEnabled: true,
                    isExactRoute: true,
                    onClick: loginBeforeNavigate,
                },
                {
                    key: AccountSidebarItem.DocumentUploadV1,
                    label: 'document upload',
                    iconName: 'data-file',
                    to: RouteName.Account.DocumentUpload,
                    isEnabled: !!user.value?.isVerifiedIdCard && !getAccountConfig().isCanonKycEnabled,
                    isExactRoute: true,
                    onClick: loginBeforeNavigate,
                },
                {
                    key: AccountSidebarItem.DocumentUploadV2,
                    label: 'identity document verification',
                    iconName: 'human-user-id',
                    to: RouteName.Account.DocumentUpload,
                    isEnabled: accountIndex.state('isIdCardVerificationPending').value && !getAccountConfig().isCanonKycEnabled,
                    onClick: loginBeforeNavigate,
                },
                {
                    key: AccountSidebarItem.IdentityVerification,
                    label: 'pam.account.identityVerification',
                    iconName: 'human-user-id',
                    to: RouteName.Account.IdentityVerification,
                    isEnabled: getAccountConfig().isCanonKycEnabled,
                    onClick: clickIdCardVerification,
                },
                {
                    key: AccountSidebarItem.ValidatePhone,
                    label: 'pam.account.validatePhone',
                    iconName: 'technology-phone-number-verfication',
                    to: '',
                    isActive: false,
                    isEnabled: !!user.value?.shouldVerifySms,
                    onClick: verifyPhone,
                },
                {
                    key: AccountSidebarItem.ResponsibleGaming,
                    label: 'responsible gaming',
                    iconName: 'games-responsible-gambling',
                    to: RouteName.Account.ResponsibleGaming,
                    isEnabled: hasResponsibleGaming.value,
                    onClick: loginBeforeNavigate,
                },
                {
                    isDivider: true,
                    key: AccountSidebarItem.DividerSection2,
                },
                {
                    key: AccountSidebarItem.Settings,
                    label: 'settings',
                    iconName: 'navigation-settings',
                    isActive: isSettingsItemActive.value,
                    to: RouteName.Account.Settings,
                    isEnabled: true,
                },
                {
                    key: AccountSidebarItem.HelpAndSupport,
                    label: 'pam.account.helpAndSupport',
                    iconName: 'status-help',
                    to: RouteName.Account.HelpAndSupport,
                    isEnabled: true,
                },
            ])
        ));

        return {
            RouteName,
            isLogoutVisible: computed(() => !!user.value),
            onLogoutClick,
            stickyConfig,
            sidebarItems,
        };
    },
    components: {
        SdsSidebar,
        SdsSidebarItem,
        SdsDividerLine,
    },
});
